import React from "react";
import "./style.css";

export default function Homepage() {
  return (
    <div className="min-h-screen bg-[#0e1118] text-white">
      {/* Skip link */}
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 bg-white text-black px-3 py-2 rounded">Skip to content</a>

      {/* Header / Nav */}
      <header className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-[#0e1118]/70 bg-[#0e1118]/90 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="#home" className="flex items-center gap-3">
              <LogoMark className="h-7 w-7" />
              <span className="tracking-[0.2em] text-sm font-semibold">NATIVE BRILLIANCE</span>
            </a>
            <nav className="hidden md:flex items-center gap-6 text-sm text-white/80">
              <a href="#why" className="hover:text-white">Why</a>
              <a href="#/how-it-works" className="hover:text-white">How it Works</a>
              <a href="#types" className="hover:text-white">Types</a>
              <a href="#path" className="hover:text-white">Path</a>
              <a href="/assessment" className="inline-flex items-center rounded-xl bg-[#f277c6] px-4 py-2 font-medium text-black hover:brightness-110">Start Assessment</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="home" className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 left-1/2 h-[40rem] w-[40rem] -translate-x-1/2 rounded-full bg-[#f277c6]/10 blur-3xl" />
          <div className="absolute bottom-[-10rem] right-[-10rem] h-[28rem] w-[28rem] rounded-full bg-white/5 blur-2xl" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <div className="mx-auto mb-6 flex items-center justify-center">
            <LogoFull className="h-16 md:h-20" />
          </div>
          <h1 className="mx-auto max-w-3xl text-4xl md:text-6xl font-extrabold leading-tight">
            Discover Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f277c6] to-white">Native Brilliance</span>
          </h1>
          <p className="mt-2 text-[#f277c6] font-semibold tracking-widest uppercase">Go Native!</p>
          <p className="mx-auto mt-5 max-w-2xl text-base md:text-lg text-white/80">
            Your brain isn't broken—it's brilliant. Learn your unique cognitive architecture and how modern stress dims it—and get a clear path to restore your natural resonance.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a href="#/assessment" className="inline-flex items-center rounded-2xl bg-[#f277c6] px-6 py-3 text-black font-semibold shadow-lg shadow-[#f277c6]/30 hover:brightness-110">Start Your Assessment</a>
            <a href="#/how-it-works" className="inline-flex items-center rounded-2xl border border-white/20 px-6 py-3 font-semibold text-white/90 hover:bg-white/5">How it Works</a>
          </div>
        </div>
      </section>

      <main id="main" className="relative">
        {/* WHY */}
        <section id="why" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold">What if your "learning problems" are actually signs of untapped brilliance?</h2>
              <p className="mt-5 text-white/80">Every human brain is designed for brilliance. Your struggles aren't defects—they're clues to strengths overwhelmed by modern stressors.</p>
              <ul className="mt-6 space-y-3 text-white/80">
                <li className="flex gap-3"><Bullet /> Toxic indoor air decouples neural systems.</li>
                <li className="flex gap-3"><Bullet /> Inflammatory foods trigger chronic brain inflammation.</li>
                <li className="flex gap-3"><Bullet /> Artificial light and low natural IR disrupt cellular coherence.</li>
                <li className="flex gap-3"><Bullet /> Electromagnetic chaos and constant notifications fragment rhythms.</li>
                <li className="flex gap-3"><Bullet /> Chronic stress keeps the nervous system in threat mode.</li>
              </ul>
            </div>
            <CardGradient>
              <h3 className="text-xl font-bold tracking-tight">Electromagnetic Nature of Brilliance</h3>
              <p className="mt-3 text-white/80">Your brain is a coordinated electromagnetic system. When neural oscillations achieve harmonic resonance—like a tuned orchestra—you experience flow, focus, and effortless learning.</p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                <Pill label="Harmony" />
                <Pill label="Resonance" />
                <Pill label="Coherence" />
                <Pill label="Flow" />
                <Pill label="Focus" />
                <Pill label="Ease" />
              </div>
            </CardGradient>
          </div>
        </section>

        {/* SCIENCE / BEYOND DIAGNOSIS */}
        <section id="science" className="bg-white/5 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-10 md:grid-cols-2">
              <CardGradient>
                <h3 className="text-xl font-bold tracking-tight">Beyond Diagnosis</h3>
                <p className="mt-3 text-white/80">We precisely identify your cognitive architecture—your Native Brilliance system, electromagnetic signature, Stress Monster pattern, and an integration pathway tailored to you.</p>
                <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DiagItem title="NB System" desc="Your unique pattern of neural coordination." />
                  <DiagItem title="EM Signature" desc="Frequency combinations for optimal performance." />
                  <DiagItem title="Stress Monster" desc="How inflammation disrupts your pattern." />
                  <DiagItem title="Integration" desc="Protocols to restore harmonic resonance." />
                </dl>
              </CardGradient>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold">Validated, natural, and personalized</h2>
                <p className="mt-4 text-white/80">Our 45‑minute assessment reveals your predominant Native Brilliance system and how environment might be dimming it—no meds required. Your plan focuses on natural interventions, frequency‑specific practices, supportive environments, and integration protocols.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Badge>Clean Air</Badge>
                  <Badge>Light Hygiene</Badge>
                  <Badge>Anti‑inflammatory Nutrition</Badge>
                  <Badge>Frequency Practices</Badge>
                  <Badge>Environment by Design</Badge>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TYPES */}
        <section id="types" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <header className="mb-10 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold">Real brilliance looks different for everyone</h2>
            <p className="mt-4 text-white/80">Each system reflects a characteristic rhythm. When systems couple harmonically, your gifts compound.</p>
          </header>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <TypeCard name="Visionary" wave="25–40 Hz" color="#8b5cf6" desc="Synthesizes patterns to generate breakthrough insights." />
            <TypeCard name="Investigator" wave="15–25 Hz" color="#3b82f6" desc="Systematic analysis and quality control." />
            <TypeCard name="Integrator" wave="10–15 Hz" color="#10b981" desc="Reads emotional fields and builds bridges." />
            <TypeCard name="Guardian" wave="8–12 Hz" color="#22c55e" desc="Environmental awareness and protective wisdom." />
            <TypeCard name="Pioneer" wave="4–8 Hz" color="#eab308" desc="Kinetic exploration and rapid adaptation." />
            <TypeCard name="Accountant" wave="2–4 Hz" color="#ef4444" desc="Manages energy resources and sustainable systems." />
            <TypeCard name="Persuader" wave="β↔γ switching" color="#ffffff" darkText desc="Dynamic frequency switching for compelling communication." />
          </div>
        </section>

        {/* PATH */}
        <section id="path" className="bg-white/5 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 md:grid-cols-2 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold">The path back to brilliance</h2>
                <p className="mt-4 text-white/80">Understand your signature, apply targeted natural interventions, and practice frequency‑specific techniques. Design your environment to support coupling between systems, then iterate with integration protocols.</p>
                <ol className="mt-6 space-y-4 text-white/80 list-decimal list-inside">
                  <li>Assessment to map your electromagnetic signature</li>
                  <li>Personalized natural interventions</li>
                  <li>Frequency‑specific practices</li>
                  <li>Environment by design</li>
                  <li>Integration protocols and progress checks</li>
                </ol>
              </div>
              <CardGradient>
                <h3 className="text-xl font-bold tracking-tight">What you'll receive</h3>
                <ul className="mt-4 space-y-3 text-white/80">
                  <li className="flex gap-3"><Bullet /> A clear NB type profile and EM signature</li>
                  <li className="flex gap-3"><Bullet /> Stress Monster disruption map</li>
                  <li className="flex gap-3"><Bullet /> Natural, practical action plan</li>
                  <li className="flex gap-3"><Bullet /> Progress dashboard and check‑ins</li>
                </ul>
                <div className="mt-6">
                  <a href="#/assessment" className="inline-flex items-center rounded-2xl bg-[#f277c6] px-5 py-3 text-black font-semibold shadow-lg shadow-[#f277c6]/30 hover:brightness-110">See your plan</a>
                </div>
              </CardGradient>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="cta" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute left-[-10rem] top-[-6rem] h-[22rem] w-[22rem] rounded-full bg-[#f277c6]/20 blur-3xl" />
            <div className="absolute right-[-10rem] bottom-[-8rem] h-[22rem] w-[22rem] rounded-full bg-white/10 blur-2xl" />
          </div>
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h2 className="text-3xl md:text-4xl font-bold">Ready to discover how brilliant you actually are?</h2>
            <p className="mt-4 text-white/80">In 45 minutes you'll know your cognitive architecture, what's dimming it, and how to restore it—naturally.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a href="#/assessment" className="inline-flex items-center rounded-2xl bg-[#f277c6] px-6 py-3 text-black font-semibold shadow-lg shadow-[#f277c6]/30 hover:brightness-110">Start the Assessment</a>
              <a href="#" className="inline-flex items-center rounded-2xl border border-white/20 px-6 py-3 font-semibold text-white/90 hover:bg-white/5">Download Info Packet</a>
            </div>
            <p className="mt-6 text-xs text-white/60">No medication. No labels. Just science, resonance, and a roadmap.</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <LogoMark className="h-6 w-6" />
              <span className="text-sm tracking-widest text-white/70">NATIVE BRILLIANCE · GO NATIVE!</span>
            </div>
            <p className="text-xs text-white/50">© {new Date().getFullYear()} Streamline Learning. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* --- Shared UI --- */
function LogoMark({ className = "h-8 w-8" }) {
  return <img src="/logo-mark.png" alt="Native Brilliance symbol" className={className} />;
}

function LogoFull({ className = "h-10" }) {
  return <img src="/logo-full.png" alt="Native Brilliance — Go Native!" className={className} />;
}

function Bullet() {
  return <span className="mt-1 inline-block h-2 w-2 rounded-full bg-[#f277c6] flex-shrink-0" aria-hidden></span>;
}

function Pill({ label }) {
  return <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/80">{label}</span>;
}

function Badge({ children }) {
  return <span className="inline-flex items-center rounded-md bg-white/5 px-2.5 py-1 text-xs ring-1 ring-inset ring-white/10">{children}</span>;
}

function CardGradient({ children }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#f277c6]/10 via-transparent to-white/5"></div>
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function DiagItem({ title, desc }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-1 text-sm text-white/75">{desc}</div>
    </div>
  );
}

function TypeCard({ name, wave, desc, color = "#ffffff", darkText = false }) {
  const chipStyle = { backgroundColor: color, color: darkText ? "#0e1118" : "#0e1118" };
  return (
    <div className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:bg-white/10">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold">{name}</h3>
        <span className="ml-3 inline-flex h-6 items-center justify-center rounded-full px-2 text-xs font-semibold" style={chipStyle}>{wave}</span>
      </div>
      <p className="mt-3 text-white/80">{desc}</p>
    </div>
  );
}
